import React, { useState, useEffect } from "react";
import { collection, onSnapshot, query, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase"; // Verifique o caminho para sua config do Firebase
import { useAuth, usePermissions } from "./AuthContext";
import { User, Crown, Eye, Send, X, Mic, MicOff, Video, VideoOff } from "lucide-react";
import type DyteClient from "@dytesdk/web-core";

// Tipagem para os dados de um participante
interface Participant {
  id: string; // O UID do usuário
  name: string;
  roleInSession: "representative" | "observer";
  avatarId: string | null;
  role: string; // Role original do usuário (paciente, terapeuta, etc.)
  promotionStatus?: "invited" | "accepted" | "declined"; // Estado do convite
}

interface ParticipantsPanelProps {
  roomId: string;
  onKickUser?: (userId: string) => Promise<void>;
  onDisableAudio?: (userId: string) => Promise<void>;
  onDisableVideo?: (userId: string) => Promise<void>;
  meetingClient?: DyteClient | null;
}

const ParticipantsPanel: React.FC<ParticipantsPanelProps> = ({ roomId, onKickUser, onDisableAudio, onDisableVideo, meetingClient }) => {
  const { user } = useAuth();
  const { userRole } = usePermissions();
  const [representatives, setRepresentatives] = useState<Participant[]>([]);
  const [observers, setObservers] = useState<Participant[]>([]);
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);
  const [roomCreatedBy, setRoomCreatedBy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [participantStates, setParticipantStates] = useState<Record<string, { audioEnabled: boolean; videoEnabled: boolean }>>({});
  const [activeMeetingUserIds, setActiveMeetingUserIds] = useState<Set<string>>(new Set());

  // Efeito para obter o criador da sala
  useEffect(() => {
    if (!roomId) return;

    const roomRef = doc(db, "rooms", roomId);
    const unsubscribe = onSnapshot(roomRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setRoomCreatedBy(data.createdBy || null);
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  // Efeito para ouvir as mudanças na subcoleção 'participants' em tempo real
  useEffect(() => {
    if (!roomId) return;

    const participantsColRef = collection(db, "rooms", roomId, "participants");
    const q = query(participantsColRef);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const allParticipants: Participant[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.name) {
            allParticipants.push({
              id: doc.id,
              name: data.name || "Usuário sem nome",
              roleInSession: data.roleInSession || "observer",
              avatarId: data.avatarId || null,
              role: data.role || "participant",
              promotionStatus: data.promotionStatus, // Lê o novo campo
            } as Participant);
          }
        });

        // Separa os participantes em duas listas
        const reps = allParticipants.filter((p) => p.roleInSession === "representative");
        const obs = allParticipants.filter((p) => {
          if (p.roleInSession !== "observer") {
            return false;
          }

          // Em reunião ativa, só permitir promoção de observadores realmente conectados no Dyte.
          if (meetingClient) {
            return activeMeetingUserIds.has(p.id);
          }

          return true;
        });

        reps.sort((a, b) => a.name.localeCompare(b.name));
        obs.sort((a, b) => a.name.localeCompare(b.name));

        setRepresentatives(reps);
        setObservers(obs);
      },
      (err) => {
        console.error("Erro ao buscar participantes:", err);
        setError("Não foi possível carregar a lista de participantes.");
      }
    );

    return () => unsubscribe(); // Limpa o listener ao desmontar
  }, [roomId, meetingClient, activeMeetingUserIds]);

  useEffect(() => {
    if (!meetingClient) return;

    const updateParticipantStates = () => {
      const participants = meetingClient.participants.joined.toArray();
      const states: Record<string, { audioEnabled: boolean; videoEnabled: boolean }> = {};
      const activeIds = new Set<string>();

      participants.forEach((peer) => {
        const userId = peer.customParticipantId;
        if (userId) {
          activeIds.add(userId);
          states[userId] = {
            audioEnabled: peer.audioEnabled ?? false,
            videoEnabled: peer.videoEnabled ?? false,
          };
        }
      });

      setParticipantStates(states);
      setActiveMeetingUserIds(activeIds);
    };

    updateParticipantStates();

    // Single handler for all media updates
    const handleMediaUpdate = () => updateParticipantStates();

    // Listen at the participants collection level, not individual peers
    const participants = meetingClient.participants.joined;

    participants.on("participantJoined", updateParticipantStates);
    participants.on("participantLeft", updateParticipantStates);
    participants.on("audioUpdate", handleMediaUpdate);
    participants.on("videoUpdate", handleMediaUpdate);

    return () => {
      participants.off("participantJoined", updateParticipantStates);
      participants.off("participantLeft", updateParticipantStates);
      participants.off("audioUpdate", handleMediaUpdate);
      participants.off("videoUpdate", handleMediaUpdate);
    };
  }, [meetingClient]);

  // Função para enviar o convite de promoção
  const handlePromote = async (userIdToPromote: string) => {
    if (promotingUserId) return;

    setPromotingUserId(userIdToPromote);
    setError("");

    try {
      console.log(`✉️ Enviando convite para ${userIdToPromote} na sala ${roomId}`);
      const participantRef = doc(db, "rooms", roomId, "participants", userIdToPromote);

      // Define o 'status de promoção' como 'invited'. O Room.tsx do participante vai reagir a isto.
      await updateDoc(participantRef, {
        promotionStatus: "invited",
      });
      // A UI será atualizada automaticamente pelo listener onSnapshot,
      // então não precisamos redefinir o promotingUserId aqui.
    } catch (err: any) {
      console.error("Erro ao enviar convite:", err);
      setError("Ocorreu um erro ao enviar o convite.");
    }

    setPromotingUserId(null); // Reseta em caso de erro
  };

  // Função para despromover (voltar de representative para observer)
  const handleDemote = async (userIdToDemote: string) => {
    if (!user || userRole !== "terapeuta" || roomCreatedBy !== user.uid || userIdToDemote === user.uid) {
      return;
    }

    setError("");

    try {
      console.log(`👁️ Despromovendo ${userIdToDemote} de volta para observador na sala ${roomId}`);
      const participantRef = doc(db, "rooms", roomId, "participants", userIdToDemote);

      // Atualiza para observer e remove o avatar
      await updateDoc(participantRef, {
        roleInSession: "observer",
        avatarId: null,
        promotionStatus: null,
      });

      console.log(`✅ Usuário ${userIdToDemote} despromovido para observador`);
    } catch (err: any) {
      console.error("Erro ao despromover usuário:", err);
      setError("Ocorreu um erro ao despromover o usuário.");
    }
  };

  const handleKick = async (userIdToKick: string) => {
    if (!onKickUser || !user || userRole !== "terapeuta" || roomCreatedBy !== user.uid || userIdToKick === user.uid) {
      return;
    }

    try {
      await onKickUser(userIdToKick);
    } catch (error) {
      setError("Erro ao remover usuário da sala.");
    }
  };

  const handleDisableAudio = async (userId: string) => {
    if (!onDisableAudio || !user || userRole !== "terapeuta" || roomCreatedBy !== user.uid || userId === user.uid) {
      return;
    }

    try {
      await onDisableAudio(userId);
      // Atualiza o estado imediatamente após desativar
      if (meetingClient) {
        const participants = meetingClient.participants.joined.toArray();
        const peer = participants.find((p: any) => p.customParticipantId === userId);
        if (peer) {
          setParticipantStates((prev) => ({
            ...prev,
            [userId]: {
              audioEnabled: false,
              videoEnabled: prev[userId]?.videoEnabled ?? peer.videoEnabled ?? false,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Erro ao desativar áudio:", error);
      setError("Erro ao desativar áudio do usuário.");
    }
  };

  const handleDisableVideo = async (userId: string) => {
    if (!onDisableVideo || !user || userRole !== "terapeuta" || roomCreatedBy !== user.uid || userId === user.uid) {
      return;
    }

    try {
      await onDisableVideo(userId);
      // Atualiza o estado imediatamente após desativar
      if (meetingClient) {
        const participants = meetingClient.participants.joined.toArray();
        const peer = participants.find((p: any) => p.customParticipantId === userId);
        if (peer) {
          setParticipantStates((prev) => ({
            ...prev,
            [userId]: {
              audioEnabled: prev[userId]?.audioEnabled ?? peer.audioEnabled ?? false,
              videoEnabled: false,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Erro ao desativar vídeo:", error);
      setError("Erro ao desativar vídeo do usuário.");
    }
  };

  const getParticipantAudioState = (userId: string): boolean | null => {
    if (!meetingClient) return null;
    const state = participantStates[userId];
    if (state !== undefined) {
      return state.audioEnabled;
    }
    // Fallback para o método antigo se o estado não estiver disponível
    const participants = meetingClient.participants.joined.toArray();
    const peer = participants.find((p: any) => p.customParticipantId === userId);
    return peer?.audioEnabled ?? null;
  };

  const getParticipantVideoState = (userId: string): boolean | null => {
    if (!meetingClient) return null;
    const state = participantStates[userId];
    if (state !== undefined) {
      return state.videoEnabled;
    }
    // Fallback para o método antigo se o estado não estiver disponível
    const participants = meetingClient.participants.joined.toArray();
    const peer = participants.find((p: any) => p.customParticipantId === userId);
    return peer?.videoEnabled ?? null;
  };

  const isRoomOwner = userRole === "terapeuta" && roomCreatedBy === user?.uid;
  const canKick = isRoomOwner && !!onKickUser;
  const canDisableAudio = isRoomOwner && !!onDisableAudio && !!meetingClient;
  const canDisableVideo = isRoomOwner && !!onDisableVideo && !!meetingClient;
  const canDemote = isRoomOwner; // Pode despromover se for o dono da sala

  return (
    <div className="bg-white p-4 rounded-lg shadow-md h-full flex flex-col">
      <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Participantes</h3>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 p-2 rounded mb-4 text-sm">
          {error}
          <button onClick={() => setError("")} className="ml-2 text-red-600 hover:text-red-800 font-bold">
            ×
          </button>
        </div>
      )}

      {/* Seção de Representantes */}
      <div>
        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Crown className="h-5 w-5 text-yellow-500" />
          Representantes ({representatives.length})
        </h4>
        <ul className="space-y-2">
          {representatives.length === 0 ? (
            <li className="text-sm text-gray-500 italic">Nenhum representante ativo</li>
          ) : (
            representatives.map((p) => {
              const audioEnabled = getParticipantAudioState(p.id);
              const videoEnabled = getParticipantVideoState(p.id);
              return (
                <li key={p.id} className="flex items-center justify-between gap-3 bg-gray-50 p-2 rounded-md">
                  <div className="flex items-center gap-3 flex-1">
                    <User className="h-5 w-5 text-gray-500" />
                    <div className="flex-1">
                      <span className="text-gray-800 font-medium">{p.name}</span>
                      <div className="text-xs text-gray-500">{p.role}</div>
                    </div>
                  </div>
                  {p.id !== user?.uid && (
                    <div className="flex items-center gap-1">
                      {canDisableAudio && (
                        <button
                          onClick={() => handleDisableAudio(p.id)}
                          className={`p-1 rounded transition-colors ${
                            audioEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                          }`}
                          title={audioEnabled ? "Desativar microfone" : "Microfone desativado"}
                          disabled={!audioEnabled}>
                          {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                        </button>
                      )}
                      {canDisableVideo && (
                        <button
                          onClick={() => handleDisableVideo(p.id)}
                          className={`p-1 rounded transition-colors ${
                            videoEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                          }`}
                          title={videoEnabled ? "Desativar câmera" : "Câmera desativada"}
                          disabled={!videoEnabled}>
                          {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                        </button>
                      )}
                      {canDemote && (
                        <button
                          onClick={() => handleDemote(p.id)}
                          className="text-white text-xs font-bold px-3 py-1 rounded-full bg-orange-500 hover:bg-orange-600 transition-colors"
                          title="Voltar para observador">
                          Rebaixar
                        </button>
                      )}
                      {canKick && (
                        <button
                          onClick={() => handleKick(p.id)}
                          className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                          title="Remover da sala">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>

      {/* Seção de Observadores */}
      <div className="mt-6 pt-4 border-t flex-1 overflow-y-auto">
        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Eye className="h-5 w-5 text-blue-500" />
          Observadores ({observers.length})
        </h4>
        <ul className="space-y-2">
          {observers.length === 0 ? (
            <li className="text-sm text-gray-500 italic">Nenhum observador</li>
          ) : (
            observers.map((p) => {
              const isPromotingThisUser = promotingUserId === p.id;
              const wasInvited = p.promotionStatus === "invited";
              const audioEnabled = getParticipantAudioState(p.id);
              const videoEnabled = getParticipantVideoState(p.id);

              return (
                <li key={p.id} className="flex items-center justify-between gap-3 bg-gray-50 p-2 rounded-md">
                  <div className="flex items-center gap-3 flex-1">
                    <User className="h-5 w-5 text-gray-500" />
                    <div className="flex-1">
                      <span className="text-gray-800 font-medium">{p.name}</span>
                      <div className="text-xs text-gray-500">{p.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePromote(p.id)}
                      disabled={isPromotingThisUser || wasInvited}
                      className={`text-white text-xs font-bold px-3 py-1 rounded-full transition-colors flex items-center gap-1.5 ${
                        isPromotingThisUser || wasInvited ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"
                      }`}
                      title={wasInvited ? "Convite já enviado" : "Promover para representante"}>
                      {isPromotingThisUser ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      ) : wasInvited ? (
                        <Send className="h-3 w-3" />
                      ) : null}
                      {isPromotingThisUser ? "Promovendo..." : wasInvited ? "Aguardando" : "Promover"}
                    </button>
                    {p.id !== user?.uid && (
                      <div className="flex items-center gap-1">
                        {canDisableAudio && (
                          <button
                            onClick={() => handleDisableAudio(p.id)}
                            className={`p-1 rounded transition-colors ${
                              audioEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                            }`}
                            title={audioEnabled ? "Desativar microfone" : "Microfone desativado"}
                            disabled={!audioEnabled}>
                            {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                          </button>
                        )}
                        {canDisableVideo && (
                          <button
                            onClick={() => handleDisableVideo(p.id)}
                            className={`p-1 rounded transition-colors ${
                              videoEnabled ? "text-green-600 hover:text-green-700" : "text-gray-600 hover:text-gray-800"
                            }`}
                            title={videoEnabled ? "Desativar câmera" : "Câmera desativada"}
                            disabled={!videoEnabled}>
                            {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                          </button>
                        )}
                        {canKick && (
                          <button
                            onClick={() => handleKick(p.id)}
                            className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                            title="Remover da sala">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

export default ParticipantsPanel;
